import React, {useState, useRef, useEffect} from "react";
import axios from "axios";
import {KeenIcon} from "@/components";
import {Modal} from "@/components/modal";
import clsx from "clsx";
import {CommonHexagonBadge} from "@/partials/common";
import {enqueueSnackbar} from "notistack";
import {LoaderType, Loading} from "@/layouts/buddy/loading";
import {CPSPromptData, tagColorMap, TagEnum} from "@/pages/buddy/types/CPSPromptData.ts";

interface CPSModalProps {
    open: boolean;
    onClose: () => void;
    onInsertText: (text: string) => void;
    defaultTags?: TagEnum[];
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const CPSModal: React.FC<CPSModalProps> = ({
                                                      open,
                                                      onClose,
                                                      onInsertText,
                                                      defaultTags = [],
                                                  }) => {
    const [search, setSearch] = useState("");
    const [selectedTags, setSelectedTags] = useState<TagEnum[]>([]);
    const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState<CPSPromptData | null>(null);
    const [editedContent, setEditedContent] = useState("");
    const [prompts, setPrompts] = useState<CPSPromptData[]>([]);
    const [loadingPrompts, setLoadingPrompts] = useState(false);
    const [hasInitializedTags, setHasInitializedTags] = useState(false);
    const tagRef = useRef<HTMLDivElement | null>(null);
    const API_URL = import.meta.env.VITE_APP_API_URL;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tagRef.current && !tagRef.current.contains(event.target as Node)) {
                setTagDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (open && !hasInitializedTags) {
            setSelectedTags(defaultTags);
            setHasInitializedTags(true);
        }
    }, [open, defaultTags, hasInitializedTags]);

    useEffect(() => {
        if (!open) {
            setHasInitializedTags(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !hasInitializedTags) return;

        const fetchPrompts = async () => {
            setLoadingPrompts(true);
            try {
                const token = localStorage.getItem("authToken");

                const params = new URLSearchParams();
                if (selectedTags.length > 0) {
                    selectedTags.forEach(tag => params.append('tags', tag));
                }

                const response = await axios.get<CPSPromptData[]>(
                    `${API_URL}/cps-prompts?${params.toString()}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                setPrompts(response.data);
            } catch (err: any) {
                enqueueSnackbar(
                    `Failed to load prompts: ${err?.message || err}`,
                    {variant: "error"}
                );
            } finally {
                setLoadingPrompts(false);
            }
        };

        fetchPrompts();
    }, [open, selectedTags, hasInitializedTags, API_URL]);

    const tags: TagEnum[] = Array.from(new Set(prompts.flatMap((p) => p.tags))) as TagEnum[];

    const filteredPrompts = prompts.filter((p) =>
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.summary.toLowerCase().includes(search.toLowerCase())
    );

    const toggleTag = (tag: TagEnum) => {
        if (defaultTags.includes(tag)) return;
        setSelectedTags((prev) =>
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
        );
    };

    const formatTag = (s: string) =>
        s
            .split("_")
            .map((word) => capitalize(word))
            .join(" ");

    return (
        <Modal open={open} onClose={onClose} className="!flex">
            <div
                className="container-fixed px-8 py-6 bg-white rounded-lg shadow-lg w-full max-w-5xl mx-auto flex flex-col gap-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <KeenIcon icon="user" className="text-3xl"/>
                        <h2 className="text-xl font-semibold">Prompt Library</h2>
                    </div>
                    <button className="btn btn-sm btn-light" onClick={onClose}>
                        Close
                    </button>
                </div>

                {!editingPrompt && (
                    <div className="card rounded-xl">
                        <div
                            className="flex items-center justify-between gap-2 p-5 bg-no-repeat bg-[length:660px_310px] upgrade-bg"
                            style={{backgroundPosition: "121% 41%"}}>
                            <div className="flex items-center gap-4">
                                <CommonHexagonBadge
                                    stroke="stroke-success-clarity"
                                    fill="fill-success-light"
                                    size="size-[50px]"
                                    badge={<KeenIcon icon="information-4" className="text-xl text-success"/>}
                                />
                                <div className="flex flex-col gap-1.5">
                                    <span className="text-base font-medium text-gray-900">
                                        Find and shape your perfect prompt
                                    </span>
                                    <div className="text-2sm text-gray-800">
                                        Browse the library for inspiration. Click <strong>Edit</strong> to tailor any
                                        prompt to your needs before inserting it.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!editingPrompt && (
                    <div className="flex gap-4 items-center">
                        <div className="relative w-2/3">
                            <KeenIcon
                                icon="magnifier"
                                className="leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"
                            />
                            <input
                                type="text"
                                placeholder="Search prompts"
                                className="input input-sm pl-8 w-full"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        <div className="relative w-1/3" ref={tagRef}>
                            <div
                                className="input input-sm w-full flex items-center justify-between cursor-pointer pl-3 pr-2"
                                onClick={() => setTagDropdownOpen((prev) => !prev)}
                            >
                                <span className="truncate">
                                    {selectedTags.length > 0
                                        ? `Tags: ${selectedTags.map(capitalize).join(", ")}`
                                        : "Filter by tags"}
                                </span>
                                <KeenIcon icon="chevron-down" className="text-gray-500 text-sm"/>
                            </div>
                            {tagDropdownOpen && (
                                <div
                                    className="absolute z-10 mt-1 bg-white border border-gray-300 rounded-md shadow-md max-h-64 overflow-y-auto w-full p-2">
                                    {tags.map((tag) => {
                                        const isDefault = defaultTags.includes(tag);
                                        return (
                                            <label
                                                key={tag}
                                                className={clsx(
                                                    "flex items-center gap-2 px-2 py-1 rounded",
                                                    isDefault ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50 cursor-pointer"
                                                )}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTags.includes(tag)}
                                                    onChange={() => toggleTag(tag)}
                                                    disabled={isDefault}
                                                    className="checkbox checkbox-primary checkbox-sm"
                                                />
                                                <span className={clsx("badge badge-sm px-2 py-1", tagColorMap[tag])}>
                                                    {formatTag(tag)}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {editingPrompt ? (
                    <>
                        <div className="card rounded-xl">
                            <div
                                className="flex items-center justify-between gap-2 p-5 bg-no-repeat bg-[length:660px_310px] upgrade-bg"
                                style={{backgroundPosition: "121% 41%"}}>
                                <div className="flex items-center gap-4">
                                    <CommonHexagonBadge
                                        stroke="stroke-success-clarity"
                                        fill="fill-success-light"
                                        size="size-[50px]"
                                        badge={<KeenIcon icon="information-4" className="text-xl text-success"/>}
                                    />
                                    <div className="flex flex-col gap-1.5">
                                        <span className="text-base font-medium text-gray-900">
                                            Find and shape your perfect prompt
                                        </span>
                                        <div className="text-2sm text-gray-800">
                                            Adjust the content to match your situation. When you're ready, insert it
                                            into your document or cancel to go back.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card pb-2.5">
                            <div className="card-header">
                                <h3 className="card-title">{editingPrompt.title}</h3>
                            </div>
                            <div className="card-body grid gap-5">
                                <textarea
                                    rows={6}
                                    className="resize-none border border-gray-400 p-3 rounded-md text-sm w-full h-40 focus:outline-none"
                                    value={editedContent}
                                    onChange={(e) => setEditedContent(e.target.value)}
                                    placeholder="Edit your prompt before inserting..."
                                />
                                <div className="flex gap-2 justify-end pt-2.5">
                                    <button className="btn btn-sm btn-light" onClick={() => setEditingPrompt(null)}>
                                        Cancel
                                    </button>
                                    <button className="btn btn-sm btn-primary" onClick={() => {
                                        onClose();
                                        onInsertText(editedContent);
                                    }}>
                                        <KeenIcon icon="plus"/>
                                        Insert Edited
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                ) : loadingPrompts ? (
                    <div className="flex justify-center py-6">
                        <Loading
                            loading={loadingPrompts}
                            loaderType={LoaderType.LOADING_FILES}
                            loadingText="Loading prompts"
                            delay={500}
                        />
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {filteredPrompts.map((prompt) => (
                            <div key={prompt.p_id} className="border rounded-lg p-4 flex flex-col gap-2 bg-base-100">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h4 className="text-md font-semibold">{prompt.title}</h4>
                                        <p className="text-sm mt-1 text-gray-700">{prompt.summary}</p>
                                        <div className="mt-2 flex gap-2 flex-wrap">
                                            {prompt.tags.map((tag) => (
                                                <span key={tag} className={clsx("badge badge-sm", tagColorMap[tag])}>
                                                    {formatTag(tag)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button className="btn btn-light btn-sm" onClick={() => {
                                            setEditingPrompt(prompt);
                                            setEditedContent(prompt.content);
                                        }}>
                                            <KeenIcon icon="notepad-edit"/>
                                            Edit
                                        </button>
                                        <button className="btn btn-sm btn-primary" onClick={() => {
                                            onClose();
                                            onInsertText(prompt.content);
                                        }}>
                                            <KeenIcon icon="plus"/>
                                            Insert
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {filteredPrompts.length === 0 && (
                            <p>No prompts found.</p>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};
